import { computed, Inject, Injectable, LOCALE_ID, Signal, signal, WritableSignal } from '@angular/core';
import qrcode from 'qrcode';
import { authenticator } from '@otplib/preset-default';
import { ApiService, updateDashboardAPI } from '../api/api.service';
import { CustomerOidProfileSnapshot, KeyValuePair } from '../backend-types/misc-types';
import { BallotListService } from '../ballot-list/ballot-list.service';
import { Ballot, ElectorAndBallot } from '../ballot/backend-types';
import { IdvStatusWithEvents } from '../dashboard/verification-panel/verification-panel.component';
import { ApiAboutService } from './api-about.service';
import { DialogService, DialogType } from './dialog.service';

@Injectable({
  providedIn: 'root'
})
export class ElectorDataService {
  user: Elector | undefined;
  userAuthData: WritableSignal<AuthData | undefined> = signal(undefined);
  userType: UserType = 'VWA_NO_ID';
  authAppURL: WritableSignal<string | undefined> = signal(undefined);
  qrCodeURL: WritableSignal<string | undefined> = signal(undefined);
  mfaExpirationTimeMinutes: Signal<number> = computed(()=>(this.userAuthData()?.mfaExpirationTime ?? 0)/60000);
  resendExpirationTimeMinutes: Signal<number> = computed(()=>(this.userAuthData()?.resendExpirationTime ?? 0)/60000);
  idvStatus: WritableSignal<IdvStatus> = signal('NONE');
  idvTimestamp: WritableSignal<string | undefined> = signal(undefined);
  orgVerifications: WritableSignal<CustomerOidProfileSnapshot[]> = signal([]);
  receiptCode = signal<string | undefined>(undefined);
  showSpinner = signal<boolean>(false);
  dialogTitle?: string;
  dialogText?: string;
  dashboardUpdateLockTimeSeconds: number = 0;
  dashboardUpdateLockoutTimer: any;
  lastDashboardUpdate: number = 0;
  orgVerifyAttemptsRemaining: number = 3;
  orgVerifyLockoutTimer: any;
  orgVerifyLockTimeKey = 'OrgVerifyLockTime';
  orgVerifyAttemptsRemainingKey = 'OrgVerifyAttemptsRemaining';
  orgVerifyQuarantimeTime = 900000;
  
  MAX_OTP_ATTEMPTS = 5;
  MAX_OTP_RESENDS = 5;
  MAX_PASSWORD_ATTEMPTS = 10;
  MAX_RESET_ATTEMPTS = 10;

  constructor(private readonly apiService: ApiService,
    private readonly aboutService: ApiAboutService,
    private readonly ballotListService: BallotListService,
    private readonly dialogService: DialogService,
    @Inject(LOCALE_ID) readonly locale: string) { }

  get idvStatusInfo(): { idv_status: string; idv_status_timestamp: string | undefined } {
    return {
      idv_status: this.idvStatus(),
      idv_status_timestamp: this.idvTimestamp(),
    };
  }

  setIdvStatus(elector?: Elector){
    this.idvStatus.set(elector?.idv_status ?? "NONE");
    this.idvTimestamp.set(elector?.idv_status_timestamp);
  }

  addOrgVerification(org: CustomerOidProfileSnapshot) {
    let orgs: number | undefined = this.orgVerifications().find((verification) => verification.organizationId === org.organizationId)?.organizationId;
    if(!orgs){
      this.orgVerifications().push(org);
    }
  }

  updateDashboard() {
    this.showSpinner.set(true);
    this.apiService.postWithHandshakeCheck<IdvStatusWithEvents>(updateDashboardAPI,
      {
        voter_id: this.userAuthData()?.voter_id
      }).subscribe({
      next: (resp) => {
        if(resp.body) {
          this.dashboardUpdateLockTimeSeconds = resp.body.dashboardUpdateLockTimeSeconds ?? 0;
          this.setDashboardUpdateTimer();
          this.idvStatus.set(resp.body.idv_status);
          this.idvTimestamp.set(resp.body.idv_status_timestamp);
          if(resp.body.idv_status_timestamp === undefined) {
            resp.body.idv_status_timestamp = Date.now().toString();
          }
          this.orgVerifications.set(resp.body.orgVerifications ?? []);
          this.showSpinner.set(false);
          if(resp.body.idv_status === 'SUCCESS') {
            this.dialogTitle = $localize`Success!`;
            this.dialogText = $localize`Your ID verification passed on ${this.dateReadable(resp.body.idv_status_timestamp)}.`;
            this.dialogService.openDialog(DialogType.SUCCESS, this.dialogTitle, this.dialogText);
          } else if(resp.body.idv_status === 'FAILED') {
            this.dialogTitle = $localize`ID Verification Failed!`;
            this.dialogText = $localize`Your ID verification failed on ${this.dateReadable(resp.body.idv_status_timestamp)}.`;
            this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogText);
          } else {
            this.dialogTitle = $localize`Dashboard Updated`;
            this.dialogText = '';
            this.dialogService.openDialog(DialogType.OK, this.dialogTitle, this.dialogText);
          }
        }
        let elector: Elector = {
          voter_id: this.userAuthData()?.voter_id ?? "",
          voatz_customer_id: 0,
          idv_status: resp.body?.idv_status,
          idv_status_timestamp: resp.body?.idv_status_timestamp
        }
        this.setIdvStatus(elector);
        if(resp.body?.events) {
          let electorAndBallot: ElectorAndBallot = {
            elector: elector,
            events: resp.body.events,
            votedEventInfo: resp.body.votedEvents
          }
          this.ballotListService.setBallots(electorAndBallot);
          this.aboutService.signatureRequired.next(resp?.body.events?.at(0)?.voteEventData?.optionalEventConfiguration?.submitSignature ?? false);
        }
      },
      error: (err) => {
        this.showSpinner.set(false);
        console.error(err);
        if(err.error.error_type === 'UPDATE_TOO_SOON') {
          this.dialogTitle = $localize`Please Wait.`;
          this.dialogText = $localize`You may refresh your dashboard again in ${this.dashboardUpdateLockoutTimeRemainingString()}.`;
          this.dialogService.openDialog(DialogType.ALERT, this.dialogTitle, this.dialogText);
        } else if ((err.error_type !== undefined && (err.error_type === 'DECODER_STATE_LOST' || err.error_type === 'INVALID_MTE_DECODING')) ||
        (err.error !== undefined && (err.error.error_type === 'DECODER_STATE_LOST' || err.error.error_type === 'INVALID_MTE_DECODING' ))) {
          this.dialogTitle = $localize`Error`;
          this.dialogText = $localize`We could not process your request. Please log out, log in again, and try again.`;
          this.dialogService.openDialog(DialogType.ALERT, this.dialogTitle, this.dialogText);
        } else {
          this.dialogTitle = $localize`Error`
          this.dialogText = $localize`We could not process your request. Please try again or use the "Contact Support" link for assistance.`;
          this.dialogService.openDialog(DialogType.ERROR, this.dialogTitle, this.dialogText);
        }
      },
      complete: () => {
        this.showSpinner.set(false);
      }
    });
  }

  dateReadable(date: string): string {
    return new Date(date).toLocaleString(this.locale,  
      { weekday: "long", month: "long", day: "numeric", year: "numeric", 
      hour: "numeric", minute: "numeric", second: "numeric"});
  }

  setDashboardUpdateTimer() {
    this.dashboardUpdateLockoutTimer = setTimeout(() => {
      this.dashboardUpdateLockTimeSeconds = 0;
    }, this.dashboardUpdateLockTimeSeconds*1000);
  }

  get isDashBoardUpdateLocked(): boolean {
    return this.dashboardUpdateLockTimeSeconds > 0;
  }

  resetOrgVerifyAttempts() {
    localStorage.removeItem(this.orgVerifyLockTimeKeyForUser);
    localStorage.removeItem(this.orgVerifyAttemptsRemainingKeyForUser);
    this.orgVerifyAttemptsRemaining = 3;
  }

  get orgVerifyLockTimeKeyForUser() {
    return this.orgVerifyLockTimeKey+this.userAuthData()?.voter_id;
  }

  get orgVerifyAttemptsRemainingKeyForUser() {
    return this.orgVerifyAttemptsRemainingKey+this.userAuthData()?.voter_id;
  }

  get isOrgVerificationAllowed(): boolean {
    if(this.orgVerifyAttemptsRemaining === 0 && this.orgVerifyLockoutMillisecondsRemaining() === 0){
      this.resetOrgVerifyAttempts();
      return true;
    }
    return (this.orgVerifyAttemptsRemaining > 0);
  }

  dereaseOrgVerifyAttemptsRemaining() {
    this.orgVerifyAttemptsRemaining--;
    localStorage.setItem(this.orgVerifyAttemptsRemainingKeyForUser, String(this.orgVerifyAttemptsRemaining));
  }

  orgVerifyLockoutMillisecondsRemaining(): number {
    let lockTime: string | null = localStorage.getItem(this.orgVerifyLockTimeKeyForUser);
    if(lockTime) {
      let timeDiffSinceLocking: number = Date.now() - (+lockTime);
      return this.orgVerifyQuarantimeTime - timeDiffSinceLocking;
    } else {
      return 0;
    }
  }

  orgVerifyLockoutTimeRemainingString(): string {
    let ms: number = this.orgVerifyLockoutMillisecondsRemaining();
    return this.timeRemainingString(ms);
  }
    
  timeRemainingString(ms: number) {
    let minutes: number = Math.floor(ms/60000);
    let seconds: number = Math.ceil((ms - minutes*60000)/1000);
    let timeRemaining = "";
    if(minutes > 1){
      timeRemaining = $localize`${minutes} minutes`;
      if(seconds !== 1) {
        timeRemaining += $localize` and ${seconds} seconds`;
      } else{
        timeRemaining += $localize` and 1 second`;
      }
    } else if(minutes === 1) {
      timeRemaining = $localize`1 minute`;
      if(seconds !== 1) {
        timeRemaining += $localize` and ${seconds} seconds`;
      } else{
        timeRemaining += $localize` and 1 second`;
      }
    } else {
      if(seconds !== 1) {
        timeRemaining = $localize`${seconds} seconds`;
      } else{
        timeRemaining = $localize`1 second`;
      }
    }
    return timeRemaining;
  }

  dashboardUpdateLockoutTimeRemainingString(): string {
    let diffSinceLastUpdate: number = Date.now() - this.lastDashboardUpdate;
    let ms = this.dashboardUpdateLockTimeSeconds*1000 - diffSinceLastUpdate;
    return this.timeRemainingString(ms);
  }

  startOrgVerifyLockoutTimer() {
    localStorage.setItem(this.orgVerifyLockTimeKeyForUser, String(Date.now()));
    this.orgVerifyLockoutTimer = setTimeout(() => {
      this.resetOrgVerifyAttempts();
    }, this.orgVerifyQuarantimeTime);
  }

  restartOrgVerifyLockoutTimer() {
    this.orgVerifyAttemptsRemaining = +(localStorage.getItem(this.orgVerifyAttemptsRemainingKeyForUser) ?? 3);
    let lockTime: string | null = localStorage.getItem(this.orgVerifyLockTimeKeyForUser);
    if(lockTime) {
      let timeDiffSinceLocking: number = Date.now() - (+lockTime);
      if( this.orgVerifyQuarantimeTime - timeDiffSinceLocking > 0) {
        this.orgVerifyAttemptsRemaining = 0;
        this.orgVerifyLockoutTimer = setTimeout(() => {
          this.resetOrgVerifyAttempts();
        }, this.orgVerifyQuarantimeTime - timeDiffSinceLocking);
      } else {
        this.resetOrgVerifyAttempts();        
      }
    }
  }

  getImageUrlForEventOrOrg(eventId: number){
    let event: Ballot | undefined = this.ballotListService.getBallots().find((b)=>b.eventId === eventId);
    if(event) {
      if(event.mediaData?.imageUrl) {
        return event.mediaData.imageUrl;
      } else {
        return this.orgVerifications().find((o)=>o.organizationId === event?.organizationId)?.organizationMediaData?.imageUrl;
      }
    } else {
      return undefined;
    }
  }

  getImageUrlForEvent(ballot: Ballot): string | undefined {
    if(ballot.mediaData?.imageUrl) {
      return ballot.mediaData.imageUrl;
    } else {
      return this.orgVerifications().find((o)=>o.organizationId === ballot?.organizationId)?.organizationMediaData?.imageUrl;
    }
  }

  setAuthAppSecret(id: string, token: string) {
    let url = authenticator.keyuri(id, "Voatz", token);
    this.authAppURL.set(url);
    qrcode.toDataURL(url).then(url => {
      this.qrCodeURL.set(url);
    });
    if(this.userAuthData()) {
      this.userAuthData()!.mfaToken = token;
    }
  }
  
}

export interface Elector {
  voter_id: string;
  voatz_customer_id: number;
  first_name?: string;
  last_name?: string;
  dob?: string;
  email?: string;
  phone?: string;
  idv_status?: IdvStatus;
  idv_status_timestamp?: string;
  events?: number[];
  new_user?: boolean;
  receipt_code?: string;
}

export interface AuthData {
  voter_id: string,
  mfaMethod?: MfaMethod,
  mfaVerified?: boolean,
  mfaToken?: string,
  mfaExpiration?: number,
  mfaExpirationTime?: number,
  maxResends?: number,
  resendsRemaining?: number,
  resendExpiration?: number,
  resendExpirationTime?: number,
  maxAttempts?: number,
  attemptsRemaining?: number
}



export type MfaMethod = 'SMS' | 'APP' | 'EMAIL';

export type IdvStatus = 'FAILED' | 'PENDING' | 'SUCCESS' | 'RETRY' | 'NONE' | "";

export type UserType = "VMA" | "VWA_ID" | "VWA_NO_ID";